'use client';

import { useId, useState } from 'react';

import {
  isCookieSettingsHref,
  requestCookieSettings,
} from '@eliadmin/core/src/components/dlz/cookies/cookie-settings-request';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import Link from '@eliadmin/core/src/components/link';
import type { LinkData } from '@eliadmin/core/src/utils/dlz/text';
import { cn } from '@eliadmin/core/src/utils/style-utils';

const LINK = 'dlz-body-s block w-fit text-[#B9BDC3] transition-colors hover:text-white';

/**
 * Footer link group. Below `lg` an accordion row (Figma 60367:39307 „Columns“: DLZ/Body/M title and
 * a 20 px plus, 12 + 22.4 + 12 px high), from `lg` the DLZ/UI/Label title above the plain list.
 */
export function FooterMenu({ title, links }: { title: string; links: LinkData[] }) {
  const listId = useId();
  const [open, setOpen] = useState(false);

  return (
    <nav aria-label={title} className="flex min-w-0 flex-1 flex-col lg:gap-4">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen(current => !current)}
        className="dlz-body-m flex min-h-[47px] items-center justify-between gap-4 rounded-md py-3 text-left text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-4 focus-visible:ring-offset-[#26292E] lg:hidden"
      >
        {title}
        <Icon name={open ? 'minus-20' : 'plus-20'} className="shrink-0" />
      </button>
      <p className="dlz-label text-white max-lg:hidden">
        <span className="dlz-trim block">{title}</span>
      </p>
      <ul id={listId} className={cn('flex flex-col gap-3', open ? 'max-lg:pb-4' : 'max-lg:hidden')}>
        {links.map(link => (
          <li key={`${link.href}-${link.label}`}>
            {isCookieSettingsHref(link.href) ? (
              // „Cookies“ opens the settings on the current page – an action, not a navigation (the
              // core progress bar would start on any link click); the URL is the fallback.
              <button
                type="button"
                aria-haspopup="dialog"
                onClick={() => {
                  if (!requestCookieSettings()) window.location.assign(link.href);
                }}
                className={cn(LINK, 'text-left')}
              >
                {link.label}
              </button>
            ) : (
              <Link href={link.href} prefetch={false} className={LINK}>
                {link.label}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
}
